import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import WikiHeader from "./WikiHeader";
import { ChevronDown, ChevronRight } from "lucide-react";
import WikiSidebar from "./WikiSidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WikiHome() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [username, setUsername] = useState("");
  const [archiveExpanded, setArchiveExpanded] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("wiki_token");
    
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/wiki/verify?token=${token}`);
      if (response.data.valid) {
        setIsAuthenticated(true);
        setUsername(response.data.username);
        fetchPages(token);
      } else {
        localStorage.removeItem("wiki_token");
        localStorage.removeItem("wiki_username");
      }
    } catch (error) {
      console.error("Auth check error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPages = async (token) => {
    try {
      const response = await axios.get(`${API}/wiki/pages?token=${token}`);
      setPages(response.data.pages || []);
    } catch (error) {
      console.error("Error fetching pages:", error);
    }
  };

  const handleArchiveToggle = async (pageTitle, currentlyArchived) => {
    const token = localStorage.getItem("wiki_token");
    if (!token) return;

    try {
      const response = await axios.post(
        `${API}/wiki/page/${encodeURIComponent(pageTitle)}/archive?token=${token}`
      );
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh pages list
        fetchPages(token);
      }
    } catch (error) {
      console.error("Error toggling archive:", error);
      toast.error("Failed to update page status");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("wiki_token");
    localStorage.removeItem("wiki_username");
    setIsAuthenticated(false);
    setUsername("");
    toast.success("Logged out successfully");
  };

  // Separate active and archived pages
  const activePages = pages.filter(p => !p.is_archived);
  const archivedPages = pages.filter(p => p.is_archived);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f6f6f6] flex items-center justify-center">
        <div className="text-[#72777d]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#f6f6f6]">
        <WikiHeader username="" onLogout={handleLogout} showSearch={false} />

        {/* Access Restricted Message */}
        <div className="max-w-[800px] mx-auto mt-12 px-4">
          <div className="bg-white border border-[#a2a9b1] p-6">
            <h1 className="font-wiki text-2xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-3">
              Access Restricted
            </h1>
            <p className="text-[#202122] mb-4">
              Please{" "}
              <Link to="/wiki/login" className="text-[#3366cc] hover:underline">
                log in
              </Link>{" "}
              to view this page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f6f6]">
      <WikiHeader username={username} onLogout={handleLogout} showSearch={true} />

      {/* Main Content */}
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <div className="flex gap-6">
          <WikiSidebar username={username} />

          {/* Content */}
          <main className="flex-1 bg-white border border-[#a2a9b1] p-6">
            <h1 className="font-wiki text-3xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-3">
              Main Page
            </h1>
            
            <p className="text-[#202122] mb-6">
              Welcome to the Ethical Wiki internal knowledge base. This wiki contains draft articles
              and documentation for client projects.
            </p>

            <h2 className="font-wiki text-xl text-[#202122] mt-8 mb-4 border-b border-[#a2a9b1] pb-2">
              All Pages
            </h2>
            
            {activePages.length > 0 ? (
              <ul className="list-disc list-inside space-y-2">
                {activePages.map((page) => (
                  <li key={page.title}>
                    <Link 
                      to={`/wiki/${encodeURIComponent(page.title)}`}
                      className="text-[#3366cc] hover:underline"
                      data-testid={`wiki-page-link-${page.title}`}
                    >
                      {page.title}
                    </Link>
                    <button
                      onClick={() => handleArchiveToggle(page.title, false)}
                      className="ml-2 text-sm text-[#72777d] hover:text-[#3366cc] hover:underline"
                      data-testid={`archive-btn-${page.title}`}
                    >
                      (archive)
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[#72777d] italic">No active pages.</p>
            )}

            {/* Archive Section */}
            <div className="mt-8">
              <button
                onClick={() => setArchiveExpanded(!archiveExpanded)}
                className="flex items-center gap-2 font-wiki text-xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-2 w-full text-left hover:text-[#3366cc]"
                data-testid="archive-section-toggle"
              >
                {archiveExpanded ? (
                  <ChevronDown className="w-5 h-5" />
                ) : (
                  <ChevronRight className="w-5 h-5" />
                )}
                Archive ({archivedPages.length})
              </button>
              
              {archiveExpanded && (
                <div className="pl-4">
                  {archivedPages.length > 0 ? (
                    <ul className="list-disc list-inside space-y-2">
                      {archivedPages.map((page) => (
                        <li key={page.title} className="text-[#72777d]">
                          <Link 
                            to={`/wiki/${encodeURIComponent(page.title)}`}
                            className="text-[#72777d] hover:text-[#3366cc] hover:underline"
                            data-testid={`wiki-archived-page-link-${page.title}`}
                          >
                            {page.title}
                          </Link>
                          <button
                            onClick={() => handleArchiveToggle(page.title, true)}
                            className="ml-2 text-sm text-[#72777d] hover:text-[#3366cc] hover:underline"
                            data-testid={`reactivate-btn-${page.title}`}
                          >
                            (reactivate)
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[#72777d] italic">No archived pages.</p>
                  )}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
